/**
 * Renders all currently-unlocked artifacts attached to the Spark itself
 * or its orbital ring.
 *
 * Wave 1 (MVP) — 5 artifacts:
 *   - first_crystallization_ring (spark-orbit)
 *   - tenth_crystallization_ring (spark-orbit, outer)
 *   - fiftieth_crystallization_fractal (spark)
 *   - five_rhythm_pulse (spark)
 *   - name_signature (spark)
 *
 * Wave 2 (2026-05-25) — 5 deeper artifacts:
 *   - rhythm_streak_25 (spark-orbit) — третье кольцо ритма
 *   - cryst_25_inner_ring (spark-orbit) — внутреннее кольцо памяти
 *   - spirit_50_aura (spark) — мягкая аура от 50+ духобаллов
 *   - activations_5_chorus (spark) — хор активаций (3 мини-точки)
 *   - upgrades_5_collector (spark) — венок коллекционера
 *
 * Each component returns null unless its artifact id is в `unlocked`.
 */
import { useGameStore } from '../stores/game.js';
import { useArtifactsStore } from './store.js';
import styles from './artifacts.module.css';

function OrbitRing({ outer = false }: { readonly outer?: boolean }): JSX.Element {
  const ringClass = `${styles.orbitRing ?? ''} ${outer ? (styles.orbitOuter ?? '') : ''} ${styles.artifactReveal ?? ''}`;
  const spinClass = outer ? styles.orbitSpinCCW : styles.orbitSpinCW;
  return (
    <div className={ringClass} aria-hidden>
      <svg className={`${styles.orbitRingSvg ?? ''} ${spinClass ?? ''}`} viewBox="0 0 100 100">
        <circle className={styles.orbitCircle} cx="50" cy="50" r={outer ? 53 : 48} />
      </svg>
    </div>
  );
}

function InnerFractal(): JSX.Element {
  return (
    <div className={`${styles.fractalLayer ?? ''} ${styles.artifactReveal ?? ''}`} aria-hidden>
      <svg className={styles.fractalSvg} viewBox="0 0 100 100">
        {/* Six-point inner star — two overlapping triangles */}
        <polygon className={styles.fractalLine} points="50,15 85,75 15,75" />
        <polygon className={styles.fractalLine} points="50,85 15,25 85,25" />
        <circle className={styles.fractalLine} cx="50" cy="50" r="18" />
      </svg>
    </div>
  );
}

function DoubleBreath(): JSX.Element {
  return (
    <div
      className={`${styles.doubleBreath ?? ''} ${styles.doubleBreathInner ?? ''} ${styles.artifactReveal ?? ''}`}
      aria-hidden
    />
  );
}

function NameSignature(): JSX.Element | null {
  const name = useGameStore((s) => s.state.profile.displayName);
  if (name === null || name.trim() === '') return null;
  // 2026-05-26: НЕ применяем styles.artifactReveal — у nameSig свой
  // keyframe который сохраняет translateX(-50%) (см. CSS). Иначе имя
  // съезжало вбок после fade-in.
  return (
    <div className={styles.nameSig} aria-hidden>
      {name}
    </div>
  );
}

// =========================================================================
// Wave 2 (2026-05-25) — глубокая игра, 25+ кристалл / 25+ streak / 50+ spirit
// =========================================================================

/**
 * Третье кольцо ритма — игрок достиг 25 streak. Сидит между первым
 * и десятым crystallization ring, оттенок более интенсивный (gold).
 */
function RhythmStreakRing(): JSX.Element {
  return (
    <div
      className={`${styles.rhythmRing ?? ''} ${styles.artifactReveal ?? ''} ${styles.orbitSpinCW ?? ''}`}
      aria-hidden
    >
      <svg className={styles.orbitRingSvg} viewBox="0 0 100 100">
        <circle className={styles.rhythmCircle} cx="50" cy="50" r="44" />
      </svg>
    </div>
  );
}

/**
 * Внутреннее кольцо памяти — 25 кристаллизаций. Ближе к ядру Искры,
 * чем external orbit rings. Тонкое и плотное.
 */
function InnerMemoryRing(): JSX.Element {
  return (
    <div
      className={`${styles.innerRing ?? ''} ${styles.artifactReveal ?? ''} ${styles.orbitSpinCCW ?? ''}`}
      aria-hidden
    >
      <svg className={styles.orbitRingSvg} viewBox="0 0 100 100">
        <circle className={styles.innerCircle} cx="50" cy="50" r="36" />
      </svg>
    </div>
  );
}

/**
 * Мягкая золотая аура — 50+ духобаллов. Большой radial glow вокруг
 * Искры, дышит. Не блокирует тапы (pointer-events: none на parent).
 */
function SpiritAura(): JSX.Element {
  return (
    <div className={`${styles.spiritAura ?? ''} ${styles.artifactReveal ?? ''}`} aria-hidden />
  );
}

/**
 * Хор активаций — 5 активаций Сай услышаны. Три мини-светящиеся точки
 * по орбите Искры (отличается от Guide точек — те снизу/сбоку, эти
 * по верхней арке).
 */
function ActivationsChorus(): JSX.Element {
  return (
    <div
      className={`${styles.chorusLayer ?? ''} ${styles.artifactReveal ?? ''} ${styles.orbitSpinCW ?? ''}`}
      aria-hidden
    >
      <span className={`${styles.chorusDot ?? ''} ${styles.chorusDotA ?? ''}`} />
      <span className={`${styles.chorusDot ?? ''} ${styles.chorusDotB ?? ''}`} />
      <span className={`${styles.chorusDot ?? ''} ${styles.chorusDotC ?? ''}`} />
    </div>
  );
}

/**
 * Венок коллекционера — куплено 5 апгрейдов. 8 мелких золотых
 * штрихов по окружности (как лавровый венок). Под orbit ring'ами.
 */
function CollectorWreath(): JSX.Element {
  // Generate 8 dashes at evenly spaced angles. SVG для precision.
  const dashes = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <div className={`${styles.wreathLayer ?? ''} ${styles.artifactReveal ?? ''}`} aria-hidden>
      <svg className={styles.orbitRingSvg} viewBox="0 0 100 100">
        {dashes.map((angle) => (
          <line
            key={angle}
            className={styles.wreathDash}
            x1="50"
            y1="8"
            x2="50"
            y2="14"
            transform={`rotate(${angle.toString()} 50 50)`}
          />
        ))}
      </svg>
    </div>
  );
}

export function SparkArtifacts(): JSX.Element {
  // Single subscription — Zustand re-renders us only when `unlocked` Set
  // identity changes (which it does whenever we record new unlocks).
  const unlocked = useArtifactsStore((s) => s.unlocked);

  return (
    <>
      {/* Wave 1 (MVP) */}
      {unlocked.has('first_crystallization_ring') && <OrbitRing />}
      {unlocked.has('tenth_crystallization_ring') && <OrbitRing outer />}
      {unlocked.has('fiftieth_crystallization_fractal') && <InnerFractal />}
      {unlocked.has('five_rhythm_pulse') && <DoubleBreath />}
      {unlocked.has('name_signature') && <NameSignature />}
      {/* Wave 2 (2026-05-25) */}
      {unlocked.has('rhythm_streak_25') && <RhythmStreakRing />}
      {unlocked.has('cryst_25_inner_ring') && <InnerMemoryRing />}
      {unlocked.has('spirit_50_aura') && <SpiritAura />}
      {unlocked.has('activations_5_chorus') && <ActivationsChorus />}
      {unlocked.has('upgrades_5_collector') && <CollectorWreath />}
    </>
  );
}
